import React from 'react';

import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { updateAccount } from 'src/accounts/AccountProfile/api';
import { ValidAreaCode } from 'src/accounts/constants';
import { Toast } from 'src/common/components';

import { provisionBotNumber as provisionBotNumberApi } from '../api';
import { Action } from '../constants';
import { useCreateAccountContext } from '../context';

type FormType = {
  botNumberAreaCode: string;
};

const ProvisionBotNumber: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useCreateAccountContext();
  const { account } = state;
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormType>();

  const handleNavigate = (): void => {
    navigate('/accounts');
  };

  const handleNavigateToAccount = (): void => {
    if (account?.id) {
      navigate(`../../accounts/${account?.id}/overview`);
      return;
    }
    navigate('/accounts');
  };

  const [provisionBotNumber, { isLoading }] = useMutation(provisionBotNumberApi, {
    onSuccess: () => {
      dispatch({
        type: Action.SET_STEP,
      });
    },
    onError: () => {
      Toast.error('There was an error provisioning bot number.');
    },
  });

  const handleProvisionBotNumber = async (data: FormType): Promise<void> => {
    const accountId = account?.id || '';
    try {
      await updateAccount(accountId, { bot_number_area_code: data.botNumberAreaCode });
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      provisionBotNumber({ accountId });
    } catch {
      Toast.error('There was an error updating account bot number area code.');
    }
  };

  const validateAreaCode = (value: string): boolean => {
    const areaCodes: string[] = Object.values(ValidAreaCode);
    return areaCodes.includes(String(value));
  };

  return (
    <form className="mt-12" onSubmit={handleSubmit(handleProvisionBotNumber)}>
      <h3>Account: {account?.name}</h3>
      <p className="input-label mt-4">Bot number area code </p>
      <input
        id="botNumberAreaCode"
        className="input sm:w-1/5 w-full transition duration-150 ease-in-out"
        data-testid="bot-number-area-code"
        type="number"
        autoComplete="off"
        {...register('botNumberAreaCode', {
          required: 'Bot number area code is required.',
          valueAsNumber: true,
          validate: validateAreaCode,
        })}
      />
      {errors?.botNumberAreaCode?.type === 'required' && (
        <p className="error-text" data-testid="bot-number-area-code-error">
          {errors.botNumberAreaCode?.message}
        </p>
      )}
      {errors?.botNumberAreaCode?.type === 'validate' && (
        <p className="error-text" data-testid="bot-number-area-code-error">
          Value must be a number of three digits and a valid area code.
        </p>
      )}
      <div className="sm:flex-row flex flex-col mt-8">
        <button type="button" className="btn btn-ghost" onClick={handleNavigate}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-secondary sm:ml-8 sm:mt-0 mt-4"
          onClick={handleNavigateToAccount}
        >
          Visit account
        </button>
        <button
          type="submit"
          className="btn btn-secondary sm:ml-8 sm:mt-0 mt-4"
          data-testid="provision-bot"
          disabled={isLoading}
        >
          {isLoading ? 'Provisioning bot number...' : 'Provision bot number'}
        </button>
      </div>
    </form>
  );
};

export default ProvisionBotNumber;
